// Контракт четырёх состояний экрана: Загрузка · Пусто · Ошибка · Данные
// (.claude/rules/ui-views.md). Все четыре ветки обязательны НА УРОВНЕ ТИПА: экран,
// у которого расписано только «Данные», не соберётся.
//
// Правило 11 (ПРАВИЛА.md): скелет показывается ТОЛЬКО когда данных нет даже в кэше
// прошлой сессии. Если данные есть — показываются они, какая бы загрузка ни шла фоном:
// спиннер на месте уже показанного числа запрещён.

import type { ReactElement, ReactNode } from 'react';

import type { RemixiconComponentType } from '@remixicon/react';
import { RiErrorWarningLine, RiRestartLine } from '@remixicon/react';

import * as Button from '@/shared/ui/button';
import { ApiError } from '@/shared/errors/api-error';
import { Illustration, type ИмяКартинки } from '@/shared/ui/illustration';
import { ОживлённаяКартинка, естьОживление } from '@/shared/ui/illustration/Оживлённая';
import { cnExt } from '@/utils/cn';

export interface ScreenStateProps<T> {
  /** Данные — свежие или из кэша прошлой сессии. undefined — данных не было никогда. */
  data: T | undefined;
  /** Ошибка последней загрузки; null — ошибки нет. */
  error: ApiError | null;
  /** Что именно грузили, словами: «список людей с деньгами». Звучит в состоянии ошибки. */
  subject: string;
  /** Пустые ли данные: список без строк — это «Пусто», а не «Данные». */
  isEmpty: (data: T) => boolean;
  /** Ветка «Загрузка»: скелет. Показывается только когда данных нет в кэше. */
  loading: ReactNode;
  /** Ветка «Пусто»: что пусто, почему и следующее действие (правило 7). */
  empty: ReactNode;
  /** Повтор запроса из ветки «Ошибка». */
  onRetry: () => void;
  /** Ветка «Данные». */
  children: (data: T) => ReactNode;
}

export function ScreenState<T>(props: ScreenStateProps<T>): ReactElement {
  const { data, error, subject, isEmpty, loading, empty, onRetry, children } = props;

  // Данные из кэша дороже свежей ошибки: человек продолжает работать с тем, что видел,
  // а обрыв сети не стирает экран. Свежесть показывает пометка среза в ListPane.
  if (data !== undefined) {
    return <>{isEmpty(data) ? empty : children(data)}</>;
  }
  if (error !== null) {
    return <ErrorState subject={subject} error={error} onRetry={onRetry} />;
  }
  return <>{loading}</>;
}

/**
 * Состояние «Ошибка»: что именно не приехало, конверт сервера и кнопка «Повторить».
 * «Что-то пошло не так» без указания, что пошло, состоянием ошибки не считается.
 */
export function ErrorState({
  subject,
  error,
  onRetry,
}: {
  subject: string;
  error: ApiError;
  onRetry: () => void;
}) {
  const { message, hint, next_action } = error.envelope;
  return (
    <div role='alert' className='flex w-full flex-col items-center gap-3 p-6 text-center'>
      <div className='flex size-10 shrink-0 items-center justify-center rounded-full bg-error-lighter'>
        <RiErrorWarningLine className='size-5 text-error-base' />
      </div>
      <div className='flex max-w-md flex-col gap-1'>
        <p className='text-label-sm text-text-strong-950'>Не удалось загрузить: {subject}</p>
        <p className='text-paragraph-sm text-text-sub-600'>{message}</p>
        {hint !== undefined && <p className='text-paragraph-xs text-text-sub-600'>{hint}</p>}
        {/* Следующий шаг словами — только когда он НЕ пересказывает кнопку под собой.
            Сервер почти всегда пишет «проверьте связь и нажмите «Повторить»», а кнопка
            «Повторить» стоит строкой ниже: на снимке состояния ошибки 26.08.2026 это
            выглядело как подпись к кнопке, набранная бледно-серым. Когда шаг другой
            («обратитесь к администратору»), он остаётся: это уже не повтор. */}
        {next_action !== undefined && !/повторить/i.test(next_action) && (
          <p className='text-paragraph-xs text-text-sub-600'>{next_action}</p>
        )}
      </div>
      <Button.Root variant='neutral' mode='stroke' size='small' onClick={onRetry}>
        <Button.Icon as={RiRestartLine} />
        Повторить
      </Button.Root>
    </div>
  );
}

/**
 * Состояние «Пусто»: что пусто, почему и следующее действие — а не белое поле.
 * Единственный блок пустого состояния в вебе (.claude/rules/duplication-guard.md):
 * новому экрану — новые тексты сюда, а не второй компонент.
 */
export function EmptyState({
  icon: Icon,
  illustration,
  title,
  description,
  action,
}: {
  icon?: RemixiconComponentType;
  /**
   * Картинка вместо значка — ТОЛЬКО там, где содержимого ещё не появилось.
   *
   * «Под этот отбор ничего нет», «объект закрыт», «не удалось загрузить» — другие состояния, и
   * у них остаётся значок: картинка там врёт, она успокаивает, а человеку надо снять фильтр или
   * повторить запрос. Граница и весь набор — в `shared/ui/illustration`.
   */
  illustration?: ИмяКартинки;
  /** Что пусто. */
  title: string;
  /** Почему пусто: причина, а не украшение. */
  description: ReactNode;
  /** Следующее действие: кнопка или ссылки. */
  action?: ReactNode;
}) {
  return (
    <div className='flex w-full flex-col items-center gap-3 p-6 text-center'>
      {illustration !== undefined ? (
        // Оживлённая картинка там, где для имени есть ролик; для остальных — та же
        // неподвижная. Решать это здесь, а не в каждом экране: пустых состояний восемьдесят, и
        // выбор «оживлять или нет» в каждом из них разъехался бы за неделю.
        естьОживление(illustration) ? (
          <ОживлённаяКартинка имя={illustration} />
        ) : (
          <Illustration имя={illustration} />
        )
      ) : (
        Icon !== undefined && (
          <div className='flex size-10 shrink-0 items-center justify-center rounded-full bg-bg-weak-50'>
            <Icon className='size-5 text-text-soft-400' />
          </div>
        )
      )}
      <div className='flex max-w-md flex-col gap-1'>
        <p className='text-label-sm text-text-strong-950'>{title}</p>
        <div className='text-paragraph-sm text-text-sub-600'>{description}</div>
      </div>
      {action !== undefined && (
        <div className='flex flex-wrap items-center justify-center gap-3'>{action}</div>
      )}
    </div>
  );
}

/** Кирпич скелета для ветки «Загрузка». Размеры задаёт вызывающий классами сетки. */
export function Skeleton({ className }: { className?: string }) {
  // Заливка `bg-soft-200`, а не `bg-weak-50`. Слабый оттенок совпадает с фоном правой колонки
  // и всех полноширинных представлений: на них скелет был НЕВИДИМ, и человек полторы секунды
  // смотрел на пустое серое поле, не понимая, грузится оно или сломалось. Скелет обязан
  // читаться и на белом, и на сером — иначе это не состояние загрузки, а пустота.
  return (
    <div
      aria-hidden='true'
      className={cnExt('animate-pulse rounded-md bg-bg-soft-200', className)}
    />
  );
}

/**
 * Скелет табличного представления: полоса величин и сама таблица.
 *
 * До 25.08.2026 каждое такое представление рисовало загрузку двумя прямоугольниками — полоса
 * на 64 точки и слаб на 256, — и выглядело это как две серые плиты посреди экрана. Плита не
 * говорит, ЧТО грузится: человек видит ровно столько же, сколько при поломке. Полоса величин
 * и полосы строк за то же время говорят «сейчас будет таблица», и ожидание перестаёт быть
 * ожиданием неизвестно чего.
 *
 * Строк по умолчанию восемь: столько влезает в видимую часть, и продолжать ниже незачем —
 * скелет не изображает данные, он изображает форму.
 */
export function GridSkeleton({ rows = 8, band = true }: { rows?: number; band?: boolean }) {
  return (
    <div aria-hidden='true' className='flex flex-col gap-2'>
      {band && (
        <div className='flex gap-2'>
          {[0, 1, 2, 3].map((номер) => (
            <Skeleton key={номер} className='h-11 flex-1' />
          ))}
        </div>
      )}
      <Skeleton className='h-8 w-full' />
      <div className='flex flex-col gap-1'>
        {Array.from({ length: rows }, (_, номер) => (
          <Skeleton key={номер} className='h-6 w-full' />
        ))}
      </div>
    </div>
  );
}
